import React, { Suspense, useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { CollapseProps, GlobalToken, message, Skeleton } from 'antd';

import { EPCollapse } from '@/comp';
import { useStores } from '@/hooks/use-stores';
import { useInvestigationSync } from '@/hooks/useInvestigationSync';
import { ComponentTierName } from '@/constants/enums';
import { getDynamicComponent } from '@/constants/componentsMap';

import PressureDropDevicesCollapsable from './Dashboard/PressureDropDevicesCollapsable';
import DevicesCollapsable from './Dashboard/DevicesCollapsable';

const InvestigationDashboardParams = observer(() => {
  const { t } = useTranslation();
  const { investigationStore, stationStore } = useStores();
  const { isWasteWaterStation, isLoading, stationTier, currentStation } = stationStore;
  const { sectionKeysToExpand } = useInvestigationSync(investigationStore);
  const [activeKeys, setActiveKeys] = useState<(string | number)[] | undefined>(sectionKeysToExpand);

  useEffect(() => {
    setActiveKeys(sectionKeysToExpand);
  }, [sectionKeysToExpand, currentStation.id]);

  const StationTotalCollapsible = getDynamicComponent(
    ComponentTierName.StationTotalCollapsible,
    stationTier,
    currentStation.stationPressureType,
  );

  const canSelectMetric = e => {
    if (investigationStore.totalMetricsSelected >= investigationStore.maxAllowedSelected && e.target.checked) {
      message.info(
        t(
          'You can choose up to 10 parameters per time period. When comparing 2 time periods, you can choose up to 5 parameters.',
        ),
        10,
      );
      return false;
    } else {
      return true;
    }
  };

  return (
    <Container>
      <span className="title">{t('Investigate by Devices')}</span>

      <div className="param-list">
        <Skeleton active paragraph={{ rows: 3, width: '100%' }} title={false} loading={isLoading}>
          <Suspense>
            <EPCollapse
              key={stationStore.currentStation.id}
              {...({ activeKey: activeKeys, onChange: setActiveKeys } as CollapseProps)}
            >
              <StationTotalCollapsible canSelectMetric={canSelectMetric} />
              {DevicesCollapsable(canSelectMetric)}
              {!isWasteWaterStation && !isLoading && (
                <PressureDropDevicesCollapsable canSelectMetric={canSelectMetric} />
              )}
            </EPCollapse>
          </Suspense>
        </Skeleton>
      </div>
    </Container>
  );
});

const Container = styled.div<{ theme: { token: GlobalToken } }>`
  display: flex;
  flex-direction: column;
  padding: 0 ${({ theme: { token } }) => token.paddingMD}px;
  width: 100%;
  overflow-y: auto;

  .title {
    color: ${({ theme: { token } }) => token.colorBorder};
    font-size: ${({ theme: { token } }) => token.fontSizeLG}px;
    margin: ${({ theme: { token } }) => `${token.marginLG}px 0 ${token.marginXXS}px 0`};
  }

  .ant-checkbox-wrapper {
    margin-bottom: ${({ theme: { token } }) => token.marginSM}px;
  }

  .selected-counter {
    color: ${({ theme: { token } }) => token.colorTextDisabled};
    font-size: ${({ theme: { token } }) => token.fontSize}px;
    font-weight: ${({ theme: { token } }) => token.fontWeightStrong};
  }
`;

export default InvestigationDashboardParams;
